import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Search,
  ShieldCheck,
  Download,
  AlertCircle,
  Loader,
  X,
  RotateCw,
} from "lucide-react";
import toast from "react-hot-toast";
import { Policy } from "@/types";
import { useCustomerContext } from "@/context/CustomerContext";
import { Link, useNavigate } from "react-router-dom";
import { useChat } from "@/hooks/useChat";
import { getPolicyClaimAction, rememberOpenClaim } from "@/utils/openClaims";
import { getCustomerClaims, getCustomerPolicies } from "@/service/customerServices";

const formatDate = (dateString: string) =>
  new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(dateString));

const Policies: React.FC = () => {
  const { policies, claims, isLoading, refreshData, refreshClaims } = useCustomerContext();
  const [activeTab, setActiveTab] = useState<"active" | "expired">("active");
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    void refreshClaims();
  }, []);

  const { handleOpenClaim } = useChat();
  const navigate = useNavigate();

  if (isLoading && policies.length === 0) {
    return (
      <div className="tw-min-h-screen tw-flex tw-justify-center tw-items-center tw-h-64">
        <div className="tw-w-12 tw-h-12 tw-border-t-4 tw-border-[#00C5FB] tw-rounded-full tw-animate-spin" />
      </div>
    );
  }

  // Filter logic unchanged
  const filteredPolicies = policies.filter((p: Policy) => {
    const matchesTab =
      activeTab === "expired" ? p.status === "expired" : p.status === "active";
    const matchesSearch =
      (p.name?.toLowerCase() ?? "").includes(searchQuery.toLowerCase()) ||
      (p.type?.toLowerCase() ?? "").includes(searchQuery.toLowerCase());

    return matchesTab && matchesSearch;
  });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 15 },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="tw-space-y-6 tw-bg-[#202020] tw-min-h-screen tw-p-6"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="tw-flex tw-justify-between tw-items-center"
      >
        <div>
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-100">
            My Policies
          </h1>
          <p className="tw-text-gray-300">
            Manage your active and expired insurance policies
          </p>
        </div>
      </motion.div>

      {/* Search & Tabs */}
      <motion.div
        variants={itemVariants}
        className="tw-bg-[#202020] tw-rounded-lg tw-overflow-hidden"
      >
        <div className="tw-flex tw-flex-col lg:tw-flex-row tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-gap-4">
          <div className="tw-flex tw-w-full lg:tw-w-auto tw-gap-2">
            <div className="tw-relative tw-flex-1 lg:tw-w-64">
              <Search className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-500" />
              <input
                type="text"
                placeholder="Search policies by name or type"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="tw-block tw-w-full tw-pl-10 tw-pr-3 tw-py-2 tw-bg-[#1c1c1e] tw-border tw-border-gray-700 tw-rounded-md tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-cyan-500 focus:tw-border-cyan-500 sm:tw-text-sm tw-text-gray-100"
              />
            </div>
            <button
              onClick={() => refreshData()}
              disabled={isLoading}
              className={`tw-p-2 tw-bg-[#1c1c1e] tw-border tw-border-gray-700 tw-rounded-md tw-text-gray-400 hover:tw-text-[#00C5FB] hover:tw-border-[#00C5FB] tw-transition-all ${isLoading ? "tw-opacity-50 tw-cursor-not-allowed" : ""}`}
              title="Refresh Policies"
            >
              <RotateCw className={`tw-w-5 tw-h-5 ${isLoading ? "tw-animate-spin" : ""}`} />
            </button>
          </div>

          <div className="tw-flex tw-space-x-4">
            {(["active", "expired"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-transition-colors ${
                  activeTab === tab
                    ? "tw-bg-[#2c2c2e] tw-text-[#00C5FB]"
                    : "tw-text-gray-400 hover:tw-text-cyan-300 hover:tw-bg-[#2c2c2e]"
                }`}
              >
                {tab === "active" ? "Active Policies" : "Expired Policies"}
              </button>
            ))}
          </div>
        </div>

        {/* Policy cards */}
        {filteredPolicies.length > 0 ? (
          <div className="tw-grid tw-grid-cols-1 tw-gap-6 tw-py-4">
            {filteredPolicies.map((policy) => (
              <Link
                to={`/customer/policies/${policy.id}`}
                state={{ policy }}
                key={policy.id}
                className="tw-block"
              >
                <PolicyCard
                  policy={policy}
                  {...getPolicyClaimAction(policy, claims)}
                  handleOpenClaim={handleOpenClaim}
                  navigate={navigate}
                />
              </Link>
            ))}
          </div>
        ) : (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
            <AlertCircle className="tw-w-12 tw-h-12 tw-text-gray-500" />
            <h3 className="tw-mt-2 tw-text-sm tw-font-medium tw-text-gray-100">
              No policies found
            </h3>
            <p className="tw-mt-1 tw-text-sm tw-text-gray-400">
              {searchQuery
                ? `No ${activeTab} policies matching "${searchQuery}"`
                : activeTab === "active"
                  ? "You don't have any active policies."
                  : "You don't have any expired policies."}
            </p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

interface PolicyCardProps {
  policy: Policy;
  action: "file" | "resume" | "view";
  claimId: string | null;
  handleOpenClaim: (args: {
    policyId: string;
    claimId?: string;
  }) => Promise<{ id: string }>;
  navigate: (path: string, options?: any) => void;
  /** Optional isolated data adapter. Default customer actions stay unchanged. */
  onClaimAction?: () => void | Promise<void>;
  policyReference?: string;
}

export const PolicyCard: React.FC<PolicyCardProps> = ({
  policy,
  action,
  claimId,
  handleOpenClaim,
  navigate,
  onClaimAction,
  policyReference,
}) => {
  const protectionLabel =
    policy.type === "protection"
      ? "Accidental Protection"
      : policy.type === "shipping"
        ? "Shipping Protection"
        : "Manufacturing Warranty";

  const [isCreating, setIsCreating] = useState(false);

  // Safe image parsing
  let imageObj: { src?: string; alt?: string } = {};
  if (policy.imageUrl) {
    if (typeof policy.imageUrl === "object") {
      imageObj = policy.imageUrl as { src?: string; alt?: string };
    } else if (typeof policy.imageUrl === "string") {
      try {
        const parsed = JSON.parse(policy.imageUrl);
        if (parsed && typeof parsed === "object" && "src" in parsed) {
          imageObj = parsed;
        } else {
          imageObj = { src: policy.imageUrl, alt: policy.name };
        }
      } catch {
        imageObj = { src: policy.imageUrl, alt: policy.name };
      }
    }
  }

  const handleCertificateDownload = (policy: Policy) => {
    if (!policy.certificateUrl) {
      toast.error("Certificate URL not available");
      return;
    }

    window.open(policy.certificateUrl, "_blank", "noopener,noreferrer");
    toast.success("Opening certificate...");
  };

  // First click creates a claim. Later clicks reopen it until it is submitted.
  const onFileClaimClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (isCreating) return;
    if (onClaimAction) {await onClaimAction();return;}

    if (action === "view" && claimId) {
      navigate(`/customer/claims/${claimId}`);
      return;
    }

    setIsCreating(true);

    const openExistingClaim = async (existingClaimId?: string | null) => {
      const opened = await handleOpenClaim({
        policyId: policy.id,
        claimId: existingClaimId || "",
      });
      const openedId = opened?.id || opened?.claim_id || existingClaimId;
      if (!openedId) return false;
      rememberOpenClaim(policy.id, openedId);
      navigate(`/customer/chat/${openedId}`);
      return true;
    };

    try {
      const [freshClaims, freshPolicies] = await Promise.all([
        getCustomerClaims(),
        getCustomerPolicies(),
      ]);
      const currentPolicy =
        freshPolicies.find((item) => String(item.id) === String(policy.id)) ?? policy;
      const decision = getPolicyClaimAction(currentPolicy, freshClaims);

      if (decision.action === "view" && decision.claimId) {
        navigate(`/customer/claims/${decision.claimId}`);
        return;
      }

      const opened = await openExistingClaim(decision.claimId);
      if (!opened) {
        toast.error("Could not open claim. Try again.");
        setIsCreating(false);
      }
    } catch (err: any) {
      const body = err?.response?.data ?? err ?? {};
      const record =
        body?.data && typeof body.data === "object" ? body.data : body;
      const existingId = record?.id || record?.claim_id || record?.claimId;
      const failedMode = String(record?.mode || body?.mode || "").toLowerCase();

      if (failedMode === "submitted" && existingId) {
        navigate(`/customer/claims/${existingId}`);
        return;
      }

      if (existingId || failedMode === "resume") {
        try {
          const opened = await openExistingClaim(existingId);
          if (opened) return;
        } catch {
          // The claim could not be reopened; show the server message below.
        }
      }

      const message =
        (typeof body?.message === "string" && body.message) ||
        (typeof record?.message === "string" && record.message) ||
        "Could not open claim. Try again.";
      toast.error(message);
      setIsCreating(false);
    }
  };

  // Helper to determine the cards
  const getProtectionCards = () => {
    const cardsList: { duration: string; title: string; validTill: string }[] = [];

    // Parse purchase/created date
    const createdEvent = policy.timeline?.find(t => t.event === "Policy Created");
    const pDate = createdEvent?.date
      ? new Date(createdEvent.date)
      : policy.productDetails?.purchaseDate
        ? new Date(policy.productDetails.purchaseDate)
        : policy.timeline?.[0]?.date
          ? new Date(policy.timeline[0].date)
          : new Date(new Date(policy.expiryDate).getTime() - 365 * 24 * 60 * 60 * 1000);

    const purchaseDateStr = formatDate(pDate.toISOString());

    // Parse start & end coverage dates
    const coverageStartedEvent = policy.timeline?.find(t => t.event === "Coverage Started");
    const covStartDate = coverageStartedEvent?.date 
      ? new Date(coverageStartedEvent.date)
      : pDate;
    
    const covEndDate = new Date(policy.expiryDate);

    // Calculate duration in days, months, and years
    const diffTime = covEndDate.getTime() - covStartDate.getTime();
    const diffDays = Math.round(diffTime / (24 * 60 * 60 * 1000));
    const diffMonths = Math.round(diffDays / 30.4375);
    const diffYears = Math.round(diffDays / 365);

    let durationLabel = "";
    if (diffYears >= 1 && Math.abs(diffDays - diffYears * 365) < 30) {
      durationLabel = `${diffYears}-Year`;
    } else {
      durationLabel = `${diffMonths}-Months`;
    }

    if (policy.type === 'shipping') {
      cardsList.push({
        duration: 'Transit',
        title: 'Shipping Protection',
        validTill: formatDate(policy.expiryDate),
      });
    } else if (policy.type === 'extended' || policy.type === 'manufacturing') {
      cardsList.push({
        duration: durationLabel,
        title: 'Manufacturing Warranty',
        validTill: formatDate(policy.expiryDate),
      });
    } else if (policy.type === 'protection') {
      cardsList.push({
        duration: durationLabel,
        title: 'Accidental Damage Protection',
        validTill: formatDate(policy.expiryDate),
      });
    } else {
      cardsList.push({
        duration: durationLabel,
        title: 'Product Protection',
        validTill: formatDate(policy.expiryDate),
      });
    }

    return { purchaseDateStr, cardsList };
  };

  const { purchaseDateStr, cardsList } = getProtectionCards();
  const cleanId = policy.id.replace(/^POL-/i, "");
  const formattedPolicyId = `POL-${cleanId.slice(-10).toUpperCase()}`;

  return (
    <>
      <div className="tw-relative tw-bg-[#2c2c2e] tw-border tw-border-gray-800 tw-rounded-2xl tw-p-6 tw-flex tw-flex-col md:tw-flex-row tw-items-center md:tw-items-start tw-gap-6 tw-transition-all hover:tw-border-gray-700">
        {/* Left: Product Image in White Square */}
        <div className="tw-w-[130px] tw-h-[130px] tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-bg-white tw-rounded-2xl tw-p-3">
          {imageObj.src ? (
            <img
              src={imageObj.src}
              alt={imageObj.alt || policy.name}
              className="tw-w-full tw-h-full tw-object-contain"
            />
          ) : (
            <ShieldCheck className="tw-w-10 tw-h-10 tw-text-gray-400" />
          )}
        </div>

        {/* Center: Details and protections */}
        <div className="tw-flex-1 tw-space-y-4 tw-w-full">
          <div className="tw-space-y-1">
            <h3 className="tw-text-xl tw-line-clamp-1 tw-font-bold tw-text-white">
              {policy.productName}
            </h3>
            <p className="tw-text-xs tw-text-gray-400">
              Policy ID: <span className="tw-text-gray-300">{policyReference ?? formattedPolicyId}</span> • Purchased on {purchaseDateStr}
            </p>
          </div>

          <div className="tw-space-y-2">
            <h4 className="tw-text-xs tw-font-semibold tw-text-gray-300 tw-uppercase tw-tracking-wider">
              Your Protection
            </h4>
            <div className="tw-flex tw-flex-wrap tw-gap-3">
              {cardsList.map((card, idx) => (
                <div key={idx} className="tw-bg-[#404040]/50 tw-border tw-border-gray-600/20 tw-rounded-lg tw-p-2 tw-px-3 tw-flex-none tw-w-[170px] tw-text-left">
                  <div className="tw-text-[11px] tw-font-semibold tw-text-white">
                    {card.duration}
                  </div>
                  <div className="tw-text-[11px] tw-text-gray-300 tw-truncate" title={card.title}>
                    {card.title}
                  </div>
                  <div className="tw-text-[9px] tw-text-gray-400 tw-mt-0.5">
                    Valid till {card.validTill}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Status Badge - Absolutely positioned to avoid layout crowding */}
        <span className={`tw-absolute tw-top-6 tw-right-6 tw-text-xs tw-font-semibold tw-px-2.5 tw-py-1 tw-rounded-md ${
          policy.status === 'active'
            ? 'tw-bg-[#A3F3BE] tw-text-[#14532D]'
            : policy.status === 'expired'
              ? 'tw-bg-gray-700 tw-text-gray-300'
              : 'tw-bg-yellow-950 tw-text-yellow-200'
        }`}>
          {policy.status.charAt(0).toUpperCase() + policy.status.slice(1)}
        </span>

        {/* Right: Action Buttons */}
        <div className="tw-flex tw-flex-col tw-justify-end tw-w-full md:tw-w-64 tw-mt-6 md:tw-mt-10 tw-flex-shrink-0">
          <div className="tw-flex tw-flex-col tw-space-y-2.5 tw-w-full">
            <button
              onClick={onFileClaimClick}
              disabled={isCreating}
              className="tw-w-full tw-bg-[#00C5FB] hover:tw-bg-cyan-500 tw-text-black tw-font-bold tw-px-6 tw-py-3.5 tw-rounded-xl tw-text-sm tw-flex tw-items-center tw-justify-center tw-transition-colors disabled:tw-opacity-60"
            >
              {isCreating && (
                <Loader className="tw-w-4 tw-h-4 tw-animate-spin tw-mr-2" />
              )}
              {action === "resume"
                ? "Resume your Claim"
                : action === "view"
                  ? "View Claim"
                  : "File a Claim"}
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigate(`/customer/policies/${policy.id}`, { state: { policy } });
              }}
              className="tw-w-full tw-border tw-border-gray-500 tw-text-gray-300 hover:tw-bg-[#404040]/40 tw-font-medium tw-px-6 tw-py-3 tw-rounded-xl tw-text-sm tw-flex tw-items-center tw-justify-center tw-transition-all"
            >
              View Protection Details
            </button>
          </div>
        </div>
      </div>    </>
  );
};

export default Policies;
